import { Button, Typography } from '@breetometer/components';
import { CopyIcon, InfoIcon } from 'BreetAssets';
import { Amount, EmptyWalletAddress, NotificationBanner } from 'BreetComponents';
import { alwaysRefetchApiQueryOptions, copyToClipboard, CURRENCYCONFIG, viewHelpArticle } from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';
import { openModal, useAppDispatch, useGetFeatureNoteQuery } from 'BreetRedux';
import { useCallback } from 'react';

import type { AssetWrapperRenderArgs } from './AssetInfoDetails.helpers';
import ConversionInfo from './components/ConversionInfo';
import CryptoDestinationBanner from './components/CryptoDestinationBanner';

const AssetInfoDetails = ({
	selectedAsset,
	isFetching,
	refetchData,
	walletIntro,
	hideNetworkSelection = false,
}: AssetWrapperRenderArgs) => {
	const currencyOutput = useCurrencyOutput();

	const dispatch = useAppDispatch();

	const { data: featureNotesData } = useGetFeatureNoteQuery(selectedAsset?.symbol, {
		...alwaysRefetchApiQueryOptions,
		skip: !selectedAsset?.symbol,
	});

	const featureNotes = featureNotesData?.data;

	const noteData = featureNotes?.find(({ feature, coin }) => feature === 'sell' && coin === selectedAsset?.id);

	const isWalletAddressDisabled = !selectedAsset?.isAccountBased && selectedAsset?.wallet === null;

	const onAssetGroupClick = useCallback(
		(symbol: string) => () => {
			dispatch(openModal({ id: 'assetGroup', metadata: { symbol } }));
		},
		[dispatch]
	);

	const assetSymbol = selectedAsset?.symbol ?? '';

	const isAssetGroup = assetSymbol === 'USDC' || assetSymbol === 'USDT';

	return (
		<div className='assetiteminfowrapper_details'>
			{isWalletAddressDisabled ? (
				<EmptyWalletAddress
					title={`${selectedAsset.name} Wallet address is currently inactive for your account. Please generate a new one to continue transactions.`}
					countDownTitle={`Generating ${selectedAsset.name} Wallet address securely.`}
					buttonTitle='Proceed'
					assetId={selectedAsset._id}
					isFetching={isFetching}
					refetchData={refetchData}
					mode='single'
				/>
			) : (
				<div className='appassets_iteminfosection bg-primary-500 border-primary-400'>
					<div className='appassets_iteminfosection_content'>
						<div className='assetiteminfo'>
							<div className='assetiteminfo_header theme-border-none'>
								<Typography
									as='h3'
									className='color-neutral-50'
								>
									{selectedAsset?.name} {isAssetGroup ? '' : `(${selectedAsset?.symbol})`}
								</Typography>
							</div>

							<div className='assetiteminfo_walletinfo'>
								<div className='assetiteminfo_info'>
									<InfoIcon variant='wallet' />
									<div>
										<Typography as='span'>
											{walletIntro ?? (
												<>
													Each wallet address is uniquely tied to a crypto asset. Ensure you only send the specified crypto to the
													wallet address shown.
												</>
											)}
										</Typography>
										&nbsp;&nbsp;
										<Typography
											as='a'
											onClick={viewHelpArticle('15014973')}
										>
											How it works
										</Typography>
									</div>
								</div>

								<ConversionInfo
									isAssetGroup={isAssetGroup}
									selectedAsset={selectedAsset}
								/>
							</div>

							<CryptoDestinationBanner />
							<NotificationBanner
								message={noteData?.message}
								status='error'
								type='standard'
							/>

							<div className='assetiteminfo_notice'>
								<InfoIcon
									variant='mini-wallet'
									className='svg-dark'
								/>
								<Typography
									as='p'
									className='color-primary-50'
								>
									Do not send less than {CURRENCYCONFIG.USD.symbol}
									<Amount>{currencyOutput(selectedAsset?.vipMinimum)}</Amount>
								</Typography>
							</div>

							<div className='assetiteminfo_walletaddresswrapper bg-primary-400 border-dark-navy'>
								<Typography
									as='label'
									className='color-primary-50'
								>
									Wallet Address
								</Typography>
								<div className='assetiteminfo_walletaddress bg-primary-500 border-dark-navy'>
									<Typography
										as='p'
										className='color-neutral-50'
									>
										{selectedAsset?.wallet?.address}
									</Typography>
									<CopyIcon
										variant='dark'
										focusable
										aria-label='Copy wallet address'
										role='button'
										tabIndex={0}
										onClick={copyToClipboard(
											selectedAsset?.wallet?.address,
											`${selectedAsset?.symbol} Wallet Address copied successfully`
										)}
										type='button'
										className='cursor_pointer'
									/>
								</div>
							</div>
						</div>
					</div>

					{isAssetGroup && !hideNetworkSelection && (
						<div className='appassets_iteminfosection_selectasset'>
							<Button
								variant='native'
								onClick={onAssetGroupClick(assetSymbol)}
							>
								Select Another {assetSymbol} Network
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AssetInfoDetails;
